import { useRole } from '@/hooks/useRole';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GraduationCap, Users, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const RoleSwitcher = () => {
  const { role, roles, switchRole, hasMultipleRoles, assignRole } = useRole();
  const navigate = useNavigate();

  // Always show the switcher, even with single role
  if (!role || roles.length === 0) return null;

  const handleRoleSwitch = async (newRole: 'teacher' | 'student') => {
    // If user doesn't have this role, assign it first
    if (!roles.includes(newRole)) {
      await assignRole(newRole);
    }
    
    switchRole(newRole);
    
    // Navigate to appropriate dashboard
    if (newRole === 'teacher') {
      navigate('/teacher/dashboard');
    } else {
      navigate('/student/dashboard');
    }
  };

  const getRoleIcon = (roleType: string) => {
    if (roleType === 'teacher') return <GraduationCap className="h-4 w-4" />;
    if (roleType === 'student') return <Users className="h-4 w-4" />;
    return null;
  };

  const getRoleLabel = (roleType: string) => {
    if (roleType === 'teacher') return 'Teacher';
    if (roleType === 'student') return 'Student';
    return roleType;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 font-medium">
          {getRoleIcon(role || '')}
          <span>{getRoleLabel(role || '')}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Show current roles */}
        {roles.map((roleOption) => (
          roleOption !== 'admin' && (
            <DropdownMenuItem
              key={roleOption}
              onClick={() => handleRoleSwitch(roleOption as 'teacher' | 'student')}
              className="gap-2 cursor-pointer"
              disabled={roleOption === role}
            >
              {getRoleIcon(roleOption)}
              <span>{getRoleLabel(roleOption)}</span>
              {roleOption === role && (
                <span className="ml-auto text-xs text-muted-foreground">✓</span>
              )}
            </DropdownMenuItem>
          )
        ))}
        {/* Show option to add teacher role if only student */}
        {role === 'student' && !roles.includes('teacher') && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleRoleSwitch('teacher')}
              className="gap-2 cursor-pointer"
            >
              {getRoleIcon('teacher')}
              <span>Become a Teacher</span>
            </DropdownMenuItem>
          </>
        )}
        {/* Show option to add student role if only teacher */}
        {role === 'teacher' && !roles.includes('student') && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleRoleSwitch('student')}
              className="gap-2 cursor-pointer"
            >
              {getRoleIcon('student')}
              <span>Become a Student</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
